import { CalendarBlank, ArrowRight, CaretLeft, CaretRight } from "@phosphor-icons/react";

function emptyMessage(loading) {
  if (loading) return { primary: "Searching…", secondary: "" };
  return { primary: "No results", secondary: "Try broader keywords or remove filters." };
}

export default function SearchResultsList({ loading, results, hitCount = 0, page = 0, pageSize = 25, onPageChange, onOpen }) {
  const totalPages = Math.max(1, Math.ceil(hitCount / pageSize));
  const from = page * pageSize + 1;
  const to = page * pageSize + results.length;
  const showPagination = !loading && hitCount > pageSize && results.length > 0 && onPageChange;

  return (
    <div className="mt-8 border border-zinc-200 bg-white" data-testid="search-results">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
        <div className="gf-overline">Results</div>
        <div className="gf-overline" data-testid="results-range">
          {results.length > 0 && hitCount > 0
            ? `${from.toLocaleString()}–${to.toLocaleString()} of ${hitCount.toLocaleString()}`
            : `${results.length} shown`}
        </div>
      </div>
      {(loading || results.length === 0) ? (
        <div className="p-12 text-center">
          {(() => {
            const m = emptyMessage(loading);
            return (
              <>
                <div className="gf-overline">{m.primary}</div>
                {m.secondary && <p className="text-zinc-600 mt-2 text-sm">{m.secondary}</p>}
              </>
            );
          })()}
        </div>
      ) : (
        <ul>
          {results.map((g) => (
            <li
              key={g.id}
              className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 px-6 py-5 flex flex-col md:flex-row md:items-center gap-3 justify-between cursor-pointer"
              onClick={() => onOpen(g)}
              data-testid={`grant-row-${g.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-zinc-500">{g.number}</span>
                  <span className="gf-overline">{g.agencyCode || g.agency}</span>
                  {g.status && (
                    <span className="font-mono text-xs px-2 py-0.5 bg-zinc-100 border border-zinc-200 uppercase">
                      {g.status}
                    </span>
                  )}
                </div>
                <div className="font-semibold text-zinc-900 mt-1 leading-snug">{g.title}</div>
                <div className="flex items-center gap-4 text-xs text-zinc-500 mt-2 font-mono">
                  {g.openDate && (
                    <span><CalendarBlank size={12} className="inline mr-1" /> Opens {g.openDate}</span>
                  )}
                  {g.closeDate && (
                    <span><CalendarBlank size={12} className="inline mr-1" /> Closes {g.closeDate}</span>
                  )}
                </div>
              </div>
              <ArrowRight size={20} className="text-zinc-400 shrink-0" />
            </li>
          ))}
        </ul>
      )}
      {showPagination && (
        <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-between" data-testid="pagination">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            data-testid="pagination-prev"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-blue-700 hover:underline disabled:text-zinc-300 disabled:no-underline disabled:cursor-not-allowed"
          >
            <CaretLeft size={13} weight="bold" /> Previous
          </button>
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500" data-testid="pagination-info">
            Page {(page + 1).toLocaleString()} of {totalPages.toLocaleString()}
          </div>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page + 1 >= totalPages}
            data-testid="pagination-next"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-blue-700 hover:underline disabled:text-zinc-300 disabled:no-underline disabled:cursor-not-allowed"
          >
            Next <CaretRight size={13} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}
